import React, { useState, useEffect, useContext } from 'react';
import {
  Heading,
  Banner,
  TextStyle,
  Page,
  ButtonGroup,
  Button
} from '@superops/ui-design-system';
import {
  getTranslatedText,
  mentionsContainerStyle,
  mentionsItemStyle,
  preFormatForm,
  validateForm,
  ShareReportConstraints,
  Loader,
  useActivityTracker,
  ActionGroup,
  withErrorBoundary,
  DeleteModal,
  useProduct,
  AccessControl,
  COMMON_CONSTANTS,
  DATE_PICKER_DATE_FORMAT,
  DATE_TIME_FORMAT,
  useSuperListHook,
  ALL_TABLES
} from '@nile/components';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import {
  MODIFYCREATEDTIMEENT,
  FILTERDATEMAP
} from '@nile/reports/src/modules/charts/constants';
import { GET_NOTIFICATION_TEMPLATE_META_INFO } from '@nile/banyan/src/Graphql/Mutations/templates';
import { injectIntl, FormattedMessage } from 'react-intl';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { getSubjectPlaceholdersObj } from '@nile/banyan/src/Pebbles/subjectPlaceholders';
import { MentionsInput, Mention } from 'react-mentions';
import {
  AccountClient,
  GET_CLIENTS,
  useCachedQuery,
  GET_ALL_EMPLOYEE_LIST
} from '@nile/graphql';
import { GET_COMPANY_USERS } from '@nile/graphql/src/Query/automationrules';
import { cloneDeep, omit, isEmpty, pick } from 'lodash';
import DateFilter from '@nile/reports/src/modules/reports/utils/DateFilter';
import ScheduleDuration from '@nile/client_management/src/Containers/ClientDetails/ClientContract/ScheduleDuration';
import { reactSelectInlineError } from '@nile/banyan/src/Utils/style';
import { SVGIcon } from '@superops/super-icons';
import { DEFAULT_NOTIFICATION_TEMPLATE } from '@nile/reports/src/modules/reports/constants';
import { Error } from '@nile/components/src/Utils/helpers';
import ShareContext from './ShareContext';
import { ShareFormWrapper } from './styles';
import {
  PDF_REPORT,
  SCHEDULE_TITLE,
  SHARE_TITLE,
  USER_KEY_MAP as getUserKeyMap,
  CONSTANT_MAPPING,
  METRIC_REPORT,
  DATA_REPORT_TYPE,
  RETAINER,
  INVOICE_STATEMENTS
} from '../../../../home/constants';
import ClientSection from './ClientSection';
import TechnicianSection from './TechnicianSection';
import ExternalConsultantSection from './ExternalConsultantSection';

const NewShareForm = (props) => {
  const {
    toggleSheetActive,
    intl,
    isScheduled,
    shareLoading,
    setShareLoading,
    shareData,
    schedules,
    scheduleLoading
  } = props;

  const context = useContext(ShareContext);

  const reportsContext = useContext(ReportsContext);

  const reportId = reportsContext?.activeReport?.reportId;

  const { records: retainerSummaryReportRecords } = useSuperListHook({
    tableName: ALL_TABLES.retainerSummaryReportTable,
    viewId: reportId
  });

  const { records: invoiceStatementsReportRecords } = useSuperListHook({
    tableName: ALL_TABLES.invoiceStatementsTable,
    viewId: reportId
  });

  const activeFilter =
    reportsContext.reportCondition[reportsContext.activeView];

  const filterDate =
    activeFilter && activeFilter.find((el) => el.attribute === 'createdTime');

  const filterClient =
    activeFilter && activeFilter.find((el) => el.attribute === 'client');

  const dateFromContext =
    activeFilter?.find((el) => el.attribute === 'createdTime') || {};

  const activeReportData = shareData;

  const [date, setDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);

  const [sharedClients, setSharedClients] = useState([
    { client: null, users: null }
  ]);

  const [sharedTech, setSharedTech] = useState({ users: null });
  const [allClients, setAllClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [showBanner, setShowBanner] = useState(true);
  const [errors, setErrors] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const currentProduct = useProduct();
  const isMSPProduct = useProduct('MSP');

  const [userType, setUserType] = useState(
    isMSPProduct ? 'REQUESTER' : 'TECHNICIAN'
  );

  const USER_KEY_MAP = getUserKeyMap(currentProduct);

  const currentSchedule = schedules.find((el) => el.userType === userType);

  const [scheduleDuration, setScheduleDuration] = useState({
    frequency: 'MONTHLY',
    data: { repeatInterval: '1' }
  });

  const [templateContent, setTemplateContent] = useState({});

  const { loading, data: clientList } = useCachedQuery(GET_CLIENTS, {
    client: AccountClient,
    skip: !isMSPProduct,
    variables: {
      listInfo: {
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ]
      }
    }
  });

  const { techLoading, data: companyUsersList } = useCachedQuery(
    GET_COMPANY_USERS,
    {
      client: AccountClient,
      variables: {
        listInfo: {
          sort: [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ]
        }
      }
    }
  );

  useCachedQuery(GET_NOTIFICATION_TEMPLATE_META_INFO, {
    client: AccountClient,
    variables: {
      templateType: 'REPORT',
      constant: CONSTANT_MAPPING[activeReportData?.reportType || 'METRIC']
    },
    onCompleted({ getNotificationTemplateMetaInfo }) {
      const placeHolderFields = getNotificationTemplateMetaInfo.fields.map(
        (fld) => ({
          text: getTranslatedText(intl, fld),
          value: getTranslatedText(intl, fld),
          url: fld,
          id: fld,
          display: getTranslatedText(intl, fld),
          label: getTranslatedText(intl, fld)
        })
      );
      setPlaceholders(placeHolderFields);
    }
  });

  const getAttributeName = () => {
    if (shareData?.entity && MODIFYCREATEDTIMEENT.includes(shareData?.entity)) {
      return FILTERDATEMAP[shareData?.entity];
    }
    return 'createdTime';
  };

  const updateSharedClient = (obj, ind) => {
    const updatedSharedClients = cloneDeep(sharedClients);
    const updatedClient = cloneDeep(updatedSharedClients[ind]);
    updatedSharedClients[ind] = { ...updatedClient, ...obj };
    setSharedClients(updatedSharedClients);
  };

  const updateTemplate = (obj) => {
    const updatedTemplate = {
      ...templateContent,
      ...obj
    };
    setTemplateContent(omit(updatedTemplate, ['mentions']));
  };

  const stopEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addClientHandler = () => {
    const updatedSharedClients = cloneDeep(sharedClients);
    updatedSharedClients.push({ client: null, users: null });
    setSharedClients(updatedSharedClients);
  };

  const removeClientHandler = (ind) => {
    if (sharedClients.length === 1) {
      return;
    }
    const updatedSharedClients = cloneDeep(sharedClients).filter(
      (_, i) => i !== ind
    );
    setSharedClients(updatedSharedClients);
  };

  const handleSubjectChange = (e, newVal, newPlainVal, mentions) => {
    let updatedMentions = cloneDeep(templateContent?.placeholdersUsed) || '{}';
    updatedMentions = JSON.parse(updatedMentions);
    updatedMentions.subject = getSubjectPlaceholdersObj(
      newPlainVal,
      placeholders
    );
    updatedMentions = JSON.stringify(updatedMentions);
    const obj = { subject: newPlainVal, placeholdersUsed: updatedMentions };
    const updatedTemplate = {
      ...templateContent,
      ...obj
    };
    setTemplateContent(omit(updatedTemplate, ['mentions']));
  };

  const formatUserData = (dataa) => {
    return dataa.map((userdata) => ({
      user: {
        userId: userdata?.user?.userId
      },
      client: userdata?.client
        ? {
            accountId: userdata?.client?.accountId
          }
        : null
    }));
  };

  const splitUsers = (originalData, updatedData, user) => {
    let userOnly = user === 'TECHNICIAN' ? true : false;
    let externalUser = user === 'EXTERNAL' ? true : false;
    let sharedUser = [];
    let addedClientUser = [];
    let deletedClientUser = [];

    const activeReportDataa = updatedData.map((userdata) => {
      let tempData = [];
      if (userdata.users) {
        tempData = userdata.users.map((usr) => {
          return {
            user: {
              userId: usr?.userId || null,
              ...(externalUser && {
                email: usr?.email
              })
            },
            client:
              userOnly || externalUser
                ? null
                : {
                    accountId: userdata.client.accountId
                  }
          };
        });
      }

      return tempData;
    });
    sharedUser = activeReportDataa.flat();

    if (isScheduled) {
      if (originalData.length) {
        const addedItems = sharedUser.filter(
          (el) =>
            !originalData.some((t) => {
              return userOnly
                ? t.user.userId === el.user.userId
                : t.user.userId === el.user.userId &&
                    t.client.accountId === el.client.accountId;
            })
        );
        const deletedItems = originalData.filter((el) => {
          const exist = sharedUser.some((i) => {
            return userOnly
              ? i.user.userId === el.user.userId
              : i.user.userId === el.user.userId &&
                  i.client.accountId === el.client.accountId;
          });
          return !exist;
        });
        addedClientUser = formatUserData(addedItems);
        deletedClientUser = formatUserData(deletedItems);
      } else {
        addedClientUser = sharedUser;
      }
    }
    return { sharedUser, addedClientUser, deletedClientUser };
  };

  const handleShare = () => {
    const updatedReportData = cloneDeep(activeReportData);
    const { data: scheduleObj } = scheduleDuration;

    const { name, reportId, internalName } = updatedReportData;

    if (isScheduled) {
      const validationResult = validateForm(
        preFormatForm({
          ...templateContent,
          ...scheduleObj
        }),
        ShareReportConstraints(
          scheduleDuration?.frequency,
          scheduleDuration?.data?.type,
          true
        )
      );
      if (validationResult) {
        setErrors(validationResult);
        return;
      }
    }
    let sharedUserObj = {
      sharedClients: userType === 'REQUESTER' ? sharedClients : [sharedTech]
    };

    if (userType === 'EXTERNAL' || expiryDate) {
      sharedUserObj.sharedClients[0].expiryDate = expiryDate;
    }

    let sharedUserContraint = {
      sharedClients: {
        dynamicArray: {
          length: {
            minimum: 1,
            tooShort: 'Need atleast one user'
          },
          users: {
            presence: {
              allowEmpty: false,
              message: getTranslatedText(intl, 'common.label.cannotbeempty')
            }
          },
          client: {
            presence: { allowEmpty: false }
          }
        }
      }
    };

    if (activeReportData?.reportType === INVOICE_STATEMENTS) {
      sharedUserObj = {
        ...sharedUserObj,
        date: date || null
      };
      sharedUserContraint = {
        ...sharedUserContraint,
        date: {
          presence: {
            allowEmpty: false
          }
        }
      };
    }

    const expiryDateValidation = {
      atLeastOneMinuteAhead: { field: 'Expiry Date', mandatory: true }
    };

    if (userType === 'EXTERNAL' || expiryDate) {
      sharedUserContraint.sharedClients.dynamicArray.expiryDate = expiryDateValidation;
    }

    const validateUsers = validateForm(
      preFormatForm(sharedUserObj),
      sharedUserContraint
    );
    if (!isEmpty(validateUsers)) {
      const clientErrors = validateUsers?.sharedClients?.[0]?.errors || [];
      if (validateUsers?.date?.[0]) {
        clientErrors.date = validateUsers.date[0];
      }
      setErrors(clientErrors);
      return;
    }

    setErrors(null);

    useActivityTracker({
      event: isScheduled
        ? 'REPORT_SCHEDULE_SUCCESFULL'
        : 'REPORT_SHARE_SUCCESFULL',
      data: { name: name, reportId: reportId, internalName: internalName }
    });

    const scheduleObject = {
      reportId: reportId
    };

    const { sharedUser, addedClientUser, deletedClientUser } = splitUsers(
      currentSchedule?.shareUsers || [],
      userType === 'REQUESTER' ? sharedClients : [sharedTech],
      userType
    );

    if (isScheduled) {
      if (isEmpty(currentSchedule)) {
        scheduleObject.addReportSchedule = [
          {
            scheduleDefinition: scheduleDuration,
            userType: userType,
            notificationTemplate: pick(templateContent, [
              'content',
              'placeholdersUsed',
              'subject'
            ]),
            filterCondition:
              (date && {
                attribute: getAttributeName(),
                operator: typeof date === 'string' ? 'on' : 'between',
                value: date
              }) ||
              null,
            addShareUsers: sharedUser
          }
        ];
      } else {
        scheduleObject.updateReportSchedule = [
          {
            id: currentSchedule?.id,
            scheduleDefinition: scheduleDuration,
            notificationTemplate: pick(templateContent, [
              'content',
              'placeholdersUsed',
              'subject'
            ]),
            filterCondition:
              (date && {
                attribute: getAttributeName(),
                operator: typeof date === 'string' ? 'on' : 'between',
                value: date
              }) ||
              null,
            addShareUsers: addedClientUser,
            deleteShareUsers: deletedClientUser
          }
        ];
      }
    } else {
      scheduleObject.notificationTemplate = pick(templateContent, [
        'content',
        'placeholdersUsed',
        'subject'
      ]);
      scheduleObject.shareUsers = sharedUser;

      if (activeReportData?.reportType === RETAINER) {
        const shareFilterCondition = [];

        shareFilterCondition.push({
          attribute: 'client',
          operator: 'is',
          value: sharedClients?.[0]?.client?.accountId
        });
        if (sharedClients?.[0]?.contracts?.length) {
          shareFilterCondition.push({
            attribute: 'contractId',
            operator: 'includes',
            value: sharedClients?.[0]?.contracts?.map(
              (contract) => contract?.value
            )
          });
        }
        if (date) {
          shareFilterCondition.push({
            attribute: getAttributeName(),
            operator: typeof date === 'string' ? 'on' : 'between',
            value: date
          });
        }

        scheduleObject.shareFilterCondition = {
          joinOperator: 'AND',
          operands: shareFilterCondition
        };
      } else {
        scheduleObject.shareFilterCondition =
          (date && {
            attribute: getAttributeName(),
            operator: typeof date === 'string' ? 'on' : 'between',
            value: date
          }) ||
          null;
      }

      expiryDate && (scheduleObject.expiryAt = expiryDate);
    }

    if (updatedReportData?.reportId) {
      setShareLoading(true);
      context
        .primaryAction({
          variables: {
            input: scheduleObject
          }
        })
        .finally(() => {
          setShareLoading(false);
          toggleSheetActive();
        });
    }
  };

  const resetSchedule = () => {
    if (activeReportData?.reportId) {
      setResetLoading(true);
      context
        .primaryAction({
          variables: {
            input: {
              reportId: activeReportData?.reportId,
              deleteReportSchedule: [
                {
                  id: currentSchedule?.id
                }
              ]
            }
          }
        })
        .finally(() => {
          setResetLoading(false);
        });
    }
  };

  const constructTechConfig = () => {
    if (isScheduled) {
      const data =
        currentSchedule?.shareUsers?.map((user) => ({
          ...user.user,
          label: user?.user?.name,
          value: user?.user?.userId
        })) || [];
      const techIds = [...new Set(data.map((user) => user?.userId))];
      const defaultConfig = {
        users: techIds
          .map((id) => technicians.filter((usr) => usr?.userId === id))
          .flat()
      };
      if (!isEmpty(defaultConfig)) {
        setSharedTech(defaultConfig);
      } else {
        setSharedTech({ users: null });
      }
    }
  };

  const constructClientConfig = () => {
    if (!isScheduled && filterClient) {
      const defaultConfig = [
        {
          client: allClients.find((cl) => cl.accountId === filterClient.value),
          users: null
        }
      ];
      setSharedClients(defaultConfig);
      return;
    }
    if (isScheduled) {
      const data =
        currentSchedule?.shareUsers?.map((user) => ({
          ...user.user,
          ...user.client,
          label: user?.user?.name,
          value: user?.user?.name
        })) || [];
      const clientIds = [...new Set(data.map((user) => user?.accountId))];
      const defaultConfig = clientIds.map((id) => ({
        client: allClients.find((cl) => cl.accountId === id),
        users: data.filter((usr) => usr?.accountId === id)
      }));
      if (!isEmpty(defaultConfig)) {
        setSharedClients(defaultConfig);
      } else {
        setSharedClients([{ client: null, users: null }]);
      }
    }
    if (
      reportsContext?.activeReport?.reportType === RETAINER &&
      retainerSummaryReportRecords?.length
    ) {
      setSharedClients([
        {
          client: {
            ...retainerSummaryReportRecords?.[0]?.client,
            label: retainerSummaryReportRecords?.[0]?.client?.name,
            value: retainerSummaryReportRecords?.[0]?.client?.name
          },
          users: null,
          contracts:
            retainerSummaryReportRecords?.map((el) => ({
              label: el?.contractName,
              value: el?.contractId
            })) || []
        }
      ]);
    }

    if (
      reportsContext?.activeReport?.reportType === INVOICE_STATEMENTS &&
      invoiceStatementsReportRecords?.length
    ) {
      setSharedClients(
        invoiceStatementsReportRecords?.map((record) => {
          return {
            client: {
              ...record.client,
              label: record?.client?.name,
              value: record?.client?.name
            },
            users: null
          };
        })
      );
    }
  };

  useEffect(() => {
    if (sharedClients[0]?.client && sharedClients[0]?.users) {
      const usedClients = sharedClients
        .map((cl) => cl.client?.accountId)
        .filter((cl) => !!cl);
      const updatedClients = allClients.filter(
        (cl) => !usedClients.includes(cl.accountId)
      );
      setClients(updatedClients);
    }
  }, [sharedClients]);

  useEffect(() => {
    if (allClients.length && userType === 'REQUESTER') {
      constructClientConfig();
    }
    if (
      !isEmpty(companyUsersList?.getCompanyUsersList?.userList) &&
      userType === 'TECHNICIAN'
    ) {
      constructTechConfig();
    }
  }, [allClients, userType, scheduleLoading, technicians]);

  useEffect(() => {
    const techOption = companyUsersList?.getCompanyUsersList?.userList?.map(
      (el) => ({
        ...el,
        label: `${el?.name} (${el?.email})`,
        name: el?.name,
        value: el?.userId
      })
    );
    setTechnicians(techOption);
  }, [companyUsersList]);

  useEffect(() => {
    if (clientList?.getClientList) {
      const clients = JSON.parse(
        JSON.stringify(clientList?.getClientList?.clients)
      );
      for (let i = 0; i < clients.length; i++) {
        clients[i].label = clients[i].name;
        clients[i].value = clients[i].name;
      }
      setAllClients(clients);
      setClients(clients);
    }
  }, [clientList]);

  const resetState = () => {
    setTemplateContent(
      DEFAULT_NOTIFICATION_TEMPLATE[activeReportData?.reportType || 'METRIC']
    );
    setScheduleDuration({
      frequency: 'MONTHLY',
      data: { repeatInterval: '1' }
    });
    if (activeReportData?.reportType !== INVOICE_STATEMENTS) {
      setDate(null);
    }
    if (
      reportsContext?.activeReport?.reportType !== RETAINER &&
      reportsContext?.activeReport?.reportType !== INVOICE_STATEMENTS
    ) {
      setSharedClients([{ client: null, users: null }]);
    }
    setSharedTech({ users: null });
    setExpiryDate(null);
    setErrors(null);
  };

  useEffect(() => {
    if (!isEmpty(currentSchedule) && isScheduled) {
      setTemplateContent(
        currentSchedule?.notificationTemplate ||
          DEFAULT_NOTIFICATION_TEMPLATE[activeReportData?.reportType]
      );
      setScheduleDuration(
        currentSchedule?.scheduleDefinition || {
          frequency: 'MONTHLY',
          data: { repeatInterval: '1' }
        }
      );
      setDate(currentSchedule?.filterCondition?.value || null);
    } else {
      resetState();
    }
  }, [schedules, userType]);

  useEffect(() => {
    if (!isScheduled && !isEmpty(filterDate)) {
      setDate(filterDate?.value);
    }
  }, [filterDate]);

  if (loading || scheduleLoading || techLoading) {
    return (
      <div className="flex actr jctr fw height400">
        <Loader />
      </div>
    );
  }

  return (
    <Page
      variant="primary"
      contentHeight="100px"
      title={
        <Heading element="h1" weight="Bold">
          <FormattedMessage
            id={
              isScheduled
                ? SCHEDULE_TITLE[shareData?.reportType || 'METRIC']
                : SHARE_TITLE[shareData?.reportType || 'METRIC']
            }
          />
        </Heading>
      }
      primaryAction={
        <ActionGroup
          secondaryText={<FormattedMessage id="common.cancel" />}
          secondaryAction={toggleSheetActive}
          primaryText={
            isScheduled ? (
              <FormattedMessage id="reports.report.header.schedule.share.button" />
            ) : (
              <FormattedMessage id="reports.report.share.save" />
            )
          }
          primaryAction={handleShare}
          primaryLoading={shareLoading}
          primaryIcon={<SVGIcon name="Share" color="white" />}
        />
      }
    >
      <ShareFormWrapper onClick={stopEvents}>
        <div className="fx mb20">
          <AccessControl
            productToCheck={COMMON_CONSTANTS.MSP}
            render={
              <ButtonGroup segmented>
                <Button
                  onClick={() => setUserType('REQUESTER')}
                  outline={userType === 'REQUESTER'}
                >
                  <TextStyle
                    variation={userType === 'REQUESTER' ? 'strong' : 'subdued'}
                  >
                    <FormattedMessage id="app.modules.client.client" />
                  </TextStyle>
                </Button>

                {activeReportData?.reportType !== PDF_REPORT &&
                  activeReportData?.reportType !== RETAINER &&
                  activeReportData?.reportType !== INVOICE_STATEMENTS && (
                    <Button
                      onClick={() => setUserType('TECHNICIAN')}
                      outline={userType === 'TECHNICIAN'}
                    >
                      <TextStyle
                        variation={
                          userType === 'TECHNICIAN' ? 'strong' : 'subdued'
                        }
                      >
                        <FormattedMessage id="admin.stepnavi.employee" />
                      </TextStyle>
                    </Button>
                  )}

                {(activeReportData?.reportType === METRIC_REPORT ||
                  activeReportData?.reportType === DATA_REPORT_TYPE) &&
                  !isScheduled && (
                    <Button
                      onClick={() => {
                        setUserType('EXTERNAL');
                        resetState(); // reset state to remove previous data
                      }}
                      outline={userType === 'EXTERNAL'}
                    >
                      <TextStyle
                        variation={
                          userType === 'EXTERNAL' ? 'strong' : 'subdued'
                        }
                      >
                        <FormattedMessage id="common.externalConsultant" />
                      </TextStyle>
                    </Button>
                  )}
              </ButtonGroup>
            }
          />
          {isScheduled && (
            <div className="fx f11 jend actr">
              <DeleteModal
                modalActivator={
                  <Button
                    plain
                    icon={<SVGIcon name="HistoryIcon" />}
                    disabled={isEmpty(currentSchedule) || resetLoading}
                  >
                    <FormattedMessage
                      id="reports.schedule.reset"
                      values={{
                        userType: getTranslatedText(
                          intl,
                          USER_KEY_MAP[userType]
                        )
                      }}
                    />
                  </Button>
                }
                disableActivator={isEmpty(currentSchedule) || resetLoading}
                rerenderActivator={userType}
                description={getTranslatedText(
                  intl,
                  'reports.report.deleteSchedule.description'
                )}
                handleDeleteConfirmation={resetSchedule}
                variant="confirmation"
                title={getTranslatedText(
                  intl,
                  'reports.report.deleteSchedule.header'
                )}
              />
            </div>
          )}
        </div>

        {(activeReportData?.reportType !== PDF_REPORT ||
          (!isScheduled && activeReportData?.reportType === PDF_REPORT)) && (
          <div className="fx actr br10 p1020 card-border space-between">
            {activeReportData?.reportType !== PDF_REPORT && (
              <div className="fx actr">
                <TextStyle>
                  <FormattedMessage id="common.filterBy" />
                </TextStyle>
                <div className="mlr10 w250">
                  <DateFilter setDate={setDate} date={date} />
                </div>
                <Error>{errors?.date}</Error>
              </div>
            )}
            {!isScheduled &&
              reportsContext?.activeReport?.reportType !==
                INVOICE_STATEMENTS && (
                <div className="fx actr col">
                  <div className="fx actr">
                    <TextStyle>
                      <FormattedMessage id="attribute.expiryDate" />
                    </TextStyle>
                    <div className="mlr10 w250">
                      <DateFilter
                        showDateItemSelector={false}
                        placeholderLabel="common.chooseDate"
                        setDate={setExpiryDate}
                        date={expiryDate}
                        dateFormat={DATE_PICKER_DATE_FORMAT[DATE_TIME_FORMAT]}
                        showTimeInput
                        minDateRange={new Date()} //today
                        maxDateRange={
                          new Date(
                            new Date().setDate(new Date().getDate() + 180)
                          )
                        } //plus 180 days from today
                      />
                    </div>
                  </div>
                  {errors?.[0]?.error?.expiryDate?.length > 0 &&
                    reactSelectInlineError(
                      getTranslatedText(
                        intl,
                        errors?.[0]?.error?.expiryDate[0]?.key
                      )
                    )}
                </div>
              )}
          </div>
        )}

        <>
          <div className="client-select-container mt20">
            {userType === 'REQUESTER' ? (
              <>
                {sharedClients?.map((listItem, ind) => (
                  <ClientSection
                    clients={clients}
                    intl={intl}
                    addClientHandler={addClientHandler}
                    updateSharedClient={updateSharedClient}
                    removeClientHandler={removeClientHandler}
                    data={listItem}
                    index={ind}
                    errors={errors}
                    showContracts={activeReportData?.reportType === RETAINER}
                  />
                ))}
              </>
            ) : userType === 'TECHNICIAN' ? (
              <>
                <TechnicianSection
                  technicians={technicians}
                  data={sharedTech}
                  setSharedTech={setSharedTech}
                  intl={intl}
                  errors={errors}
                  currentProduct={currentProduct}
                />
              </>
            ) : userType === 'EXTERNAL' ? (
              <>
                <ExternalConsultantSection
                  data={sharedTech}
                  setSharedTech={setSharedTech}
                  intl={intl}
                  errors={errors}
                  currentProduct={currentProduct}
                />
              </>
            ) : (
              <></>
            )}
          </div>
          <div className="share-report mb40 pt20">
            <div className="mr-10 ml-10">
              <MentionsInput
                singleLine
                value={templateContent?.subject || ''}
                onChange={handleSubjectChange}
                placeholder={getTranslatedText(
                  intl,
                  'reports.report.share.emailsubject'
                )}
                style={mentionsContainerStyle}
              >
                <Mention
                  trigger="#"
                  data={placeholders}
                  style={mentionsItemStyle}
                  displayTransform={(a, b) => `#${b}`}
                  appendSpaceOnAdd
                />
              </MentionsInput>
              {errors?.subject &&
                reactSelectInlineError(
                  getTranslatedText(intl, 'reports.share.validation.subject')
                )}
            </div>
            <EmailEditor
              template={templateContent}
              updateTemplate={updateTemplate}
              placeholders={placeholders}
              placeholder={getTranslatedText(
                intl,
                'reports.report.share.message.placeholder'
              )}
              module="REPORT"
              details={{ operation: 'CREATE' }}
            />
            {errors?.content &&
              reactSelectInlineError(
                getTranslatedText(intl, 'reports.share.validation.content')
              )}
            {showBanner && (
              <div className="m8">
                <Banner status="info" onDismiss={() => setShowBanner(false)}>
                  <TextStyle>
                    {getTranslatedText(
                      intl,
                      'admin.eventTrigger.emailTemplate.banner.message'
                    )}
                  </TextStyle>
                </Banner>
              </div>
            )}
          </div>
        </>
        {isScheduled && (
          <>
            <div className="flex actr fx-sb mb-20">
              <Heading element="h2" weight="Bold">
                {getTranslatedText(intl, 'reports.dashboard.schedule.title')}
              </Heading>
            </div>
            <div>
              <ScheduleDuration
                filteredOptions={['MONTHLY', 'WEEKLY', 'HOURLY', 'DAILY']}
                updateParent={setScheduleDuration}
                duration={scheduleDuration}
                allowTime
                disablePastDateAndTime
                scheduleMessage="schedule.dashboard.info.message"
                errors={errors}
              />
            </div>
          </>
        )}
      </ShareFormWrapper>
    </Page>
  );
};

export default injectIntl(withErrorBoundary(NewShareForm));
